import type { LiveBreakHost, LiveStream, LiveStreamSegment } from "@hobbyshopify/backend-client";
import { BaseController, Rx, type RxList, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../../../app-deps";
import { finishBreak, randomizeBreak, setCurrentSegment } from "../../../../core/ops/live-ops";
import { errorText } from "../../../../core/primitives/errors";
import { type CycleStep, type StreamCycle, streamCycle } from "../../../../core/primitives/stream-cockpit";

export class StreamCycleState {
  readonly cycle = new Rx<StreamCycle>(
    streamCycle({ streamStatus: "", segments: [], breaks: [], onAir: false, isHost: true, now: new Date() }),
  );
  readonly live = new Rx(false);
  readonly title = new Rx("");
  readonly onAir = new Rx(false);
  /** Who is in the box on screen, in spot order. */
  readonly buyers = new Rx<readonly string[]>([]);
  /** The irreversible moves are armed on the first press and done on the second. */
  readonly confirming = new Rx<null | "early">(null);
  readonly busy = new Rx(false);
  readonly error = new Rx<string | null>(null);
}

export abstract class StreamCycleActions {
  abstract press(): void;
  abstract cancelConfirm(): void;
  abstract openLineup(): void;
  abstract endStream(): void;
}

export type StreamCycleDeps = Pick<AppDeps, "client" | "authHandler">;

export interface StreamCycleHost {
  streamId: string;
  stream: Rx<LiveStream | null>;
  segments: RxList<LiveStreamSegment>;
  breaks: RxList<LiveBreakHost>;
  buyers: RxList<string>;
  onAir: Rx<boolean>;
  isHost: Rx<boolean>;
  goLive(): void;
  addMore(): void;
  openLineup(): void;
  endStream(): void;
  onChanged(): void;
}

/**
 * The whole stream as one button that always names the next thing to do.
 * The controller is the real lock: `busy` is checked before the call, not at the
 * repaint, so ten fast taps on "Yes, draw" send one draw.
 */
export class StreamCycleController extends BaseController implements StreamCycleActions {
  readonly state = new StreamCycleState();

  constructor(
    subtree: SubtreeModel,
    readonly deps: StreamCycleDeps,
    readonly host: StreamCycleHost,
  ) {
    super();
    subtree.put(StreamCycleState, this.state);
    subtree.put(StreamCycleActions, this);
    this.sync(() => {
      const stream = host.stream.value;
      this.state.live.value = stream?.status === "LIVE";
      this.state.title.value = stream?.title ?? "";
      this.state.onAir.value = host.onAir.value;
      this.state.buyers.value = host.buyers.value;
      this.state.cycle.value = streamCycle({
        streamStatus: stream?.status ?? "",
        segments: host.segments.value,
        breaks: host.breaks.value,
        onAir: host.onAir.value,
        isHost: host.isHost.value,
        now: new Date(),
      });
    }, [host.stream, host.segments, host.breaks, host.buyers, host.onAir, host.isHost]);
  }

  press = () => {
    const step = this.state.cycle.value.step;
    if (step.disabled || this.state.busy.value) return;
    if (step.confirm && this.state.confirming.value !== step.confirm) {
      this.state.confirming.value = step.confirm;
      return;
    }
    void this.#run(step);
  };

  openLineup = () => {
    this.host.openLineup();
  };

  endStream = () => {
    this.host.endStream();
  };

  cancelConfirm = () => {
    this.state.confirming.value = null;
  };

  async #run(step: CycleStep): Promise<void> {
    if (step.move === "goLive") {
      this.host.goLive();
      return;
    }
    if (step.move === "add") {
      this.host.addMore();
      return;
    }
    this.state.busy.value = true;
    this.state.error.value = null;
    const r = await this.#call(step);
    if (this.isDisposed) return;
    this.state.busy.value = false;
    if (!r.ok) {
      this.state.error.value = errorText(r.errors);
      return;
    }
    this.state.confirming.value = null;
    this.host.onChanged();
  }

  #call(step: CycleStep) {
    const streamId = this.host.streamId;
    const breakId = step.breakId ?? "";
    switch (step.move) {
      case "next":
        return setCurrentSegment({ ...this.deps, streamId, segmentId: step.segmentId });
      case "clear":
        return setCurrentSegment({ ...this.deps, streamId, segmentId: null });
      case "finish":
        return finishBreak({ ...this.deps, breakId });
      default:
        return randomizeBreak({ ...this.deps, breakId, force: step.force });
    }
  }
}
